import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
const initialState = {
    room: null,
    participants: [],
    participantsRoomId: null,
    bans: [],
    bansRoomId: null,
};
export const useRoomStore = create()(devtools((set, get) => ({
    ...initialState,
    // Room全体の管理
    setRoom: (room) => {
        if (!room) {
            set({ room: null });
            return;
        }
        // recentActiveSoulsにlastActivityを追加
        const activeSouls = room.recentActiveSouls.map(soul => ({
            ...soul,
            lastActivity: Date.now()
        }));
        set({ room: { ...room, recentActiveSouls: activeSouls } });
    },
    updateRoom: (updates) => set((state) => ({
        room: state.room ? { ...state.room, ...updates } : null
    })),
    resetRoom: () => set({ room: null, participants: [], participantsRoomId: null, bans: [], bansRoomId: null }),
    // Room基本プロパティ
    setRoomId: (id) => set((state) => ({
        room: state.room ? { ...state.room, id } : null
    })),
    setRoomName: (name) => set((state) => ({
        room: state.room ? { ...state.room, name } : null
    })),
    setRoomKeyword: (keyword) => set((state) => ({
        room: state.room ? { ...state.room, keyword } : null
    })),
    setRoomLanguage: (language) => set((state) => ({
        room: state.room ? { ...state.room, language } : null
    })),
    setRoomIsActive: (isActive) => set((state) => ({
        room: state.room ? { ...state.room, isActive } : null
    })),
    // Soulsリスト操作
    setRecentActiveSouls: (recentActiveSouls) => set((state) => ({
        room: state.room ? { ...state.room, recentActiveSouls } : null
    })),
    addRecentActiveSoul: (soul) => set((state) => {
        if (!state.room)
            return { room: null };
        const existingIndex = state.room.recentActiveSouls.findIndex(s => s.id === soul.id);
        let updatedSouls;
        if (existingIndex >= 0) {
            // 既存のSoulを更新
            updatedSouls = state.room.recentActiveSouls.map(s => s.id === soul.id ? { ...s, ...soul } : s);
        }
        else {
            // 新規Soulを追加
            updatedSouls = [...state.room.recentActiveSouls, soul];
        }
        // lastActivityで降順ソート
        updatedSouls.sort((a, b) => b.lastActivity - a.lastActivity);
        return { room: { ...state.room, recentActiveSouls: updatedSouls } };
    }),
    removeRecentActiveSoul: (soulId) => set((state) => ({
        room: state.room ? {
            ...state.room,
            recentActiveSouls: state.room.recentActiveSouls.filter(soul => soul.id !== soulId)
        } : null
    })),
    updateRecentActiveSoul: (soulId, updates) => set((state) => ({
        room: state.room ? {
            ...state.room,
            recentActiveSouls: state.room.recentActiveSouls.map(soul => soul.id === soulId ? { ...soul, ...updates } : soul)
        } : null
    })),
    clearRecentActiveSouls: () => set((state) => ({
        room: state.room ? { ...state.room, recentActiveSouls: [] } : null
    })),
    // Messagesリスト操作
    setMessages: (messages) => set((state) => ({
        room: state.room ? { ...state.room, messages } : null
    })),
    addMessage: (message) => set((state) => ({
        room: state.room ? {
            ...state.room,
            messages: [...state.room.messages, message]
        } : null
    })),
    removeMessage: (messageId) => set((state) => ({
        room: state.room ? {
            ...state.room,
            messages: state.room.messages.filter(msg => msg.id !== messageId)
        } : null
    })),
    updateMessage: (messageId, updates) => set((state) => ({
        room: state.room ? {
            ...state.room,
            messages: state.room.messages.map(msg => msg.id === messageId ? { ...msg, ...updates } : msg)
        } : null
    })),
    clearMessages: () => set((state) => ({
        room: state.room ? { ...state.room, messages: [] } : null
    })),
    // Participantsリスト操作
    setParticipants: (roomId, participants) => set({ participants, participantsRoomId: roomId }),
    addParticipant: (roomId, participant) => set((state) => {
        // 異なるroomのデータは無視
        if (state.participantsRoomId && state.participantsRoomId !== roomId) {
            return {};
        }
        const existingIndex = state.participants.findIndex(p => p.soulId === participant.soulId);
        if (existingIndex >= 0) {
            // 既存の参加者を更新（最新の訪問時刻で更新）
            const updatedParticipants = state.participants.map(p => p.soulId === participant.soulId ? { ...p, ...participant } : p);
            // lastJoinedAtで降順ソート
            updatedParticipants.sort((a, b) => b.lastJoinedAt - a.lastJoinedAt);
            return { participants: updatedParticipants, participantsRoomId: roomId };
        }
        else {
            // 新規参加者を追加
            const updatedParticipants = [participant, ...state.participants];
            return { participants: updatedParticipants, participantsRoomId: roomId };
        }
    }),
    removeParticipant: (soulId) => set((state) => ({
        participants: state.participants.filter(p => p.soulId !== soulId)
    })),
    clearParticipants: () => set({ participants: [], participantsRoomId: null }),
    // Bansリスト操作
    setBans: (roomId, bans) => set({ bans, bansRoomId: roomId }),
    addBan: (roomId, ban) => set((state) => {
        // 異なるroomのデータは無視
        if (state.bansRoomId && state.bansRoomId !== roomId) {
            return {};
        }
        // 既存のBanを確認（重複回避）
        const existingIndex = state.bans.findIndex(b => b.soulId === ban.soulId);
        if (existingIndex >= 0) {
            // 既に存在する場合は更新しない
            return { bans: state.bans };
        }
        else {
            // 新規Banを追加（最新が先頭）
            return { bans: [ban, ...state.bans], bansRoomId: roomId };
        }
    }),
    removeBan: (soulId) => set((state) => ({
        bans: state.bans.filter(b => b.soulId !== soulId)
    })),
    clearBans: () => set({ bans: [], bansRoomId: null }),
    // recentActiveSoulsのクリーンアップ（1時間以上古いものを削除）
    cleanupOldSouls: () => set((state) => {
        if (!state.room)
            return { room: null };
        const ONE_HOUR = 60 * 60 * 1000; // 1時間 = 3600000ms
        const now = Date.now();
        const filteredSouls = state.room.recentActiveSouls.filter(soul => now - soul.lastActivity < ONE_HOUR);
        return { room: { ...state.room, recentActiveSouls: filteredSouls } };
    }),
    // recentActiveSoulsをlastActivityの新しい順にソート
    sortSoulsByActivity: () => set((state) => {
        if (!state.room)
            return { room: null };
        const sortedSouls = [...state.room.recentActiveSouls].sort((a, b) => b.lastActivity - a.lastActivity);
        return { room: { ...state.room, recentActiveSouls: sortedSouls } };
    }),
}), {
    name: 'room-store',
}));
